import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'react-toastify';
import {
  Layout,
  Menu,
  Button,
  Dropdown,
} from 'antd';
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  AppstoreOutlined,
  UserOutlined,
  CarOutlined,
  LogoutOutlined,
  ClusterOutlined,
  BellOutlined,
  BarChartOutlined,
  FileTextOutlined,
  ReconciliationOutlined,
  TableOutlined,
  RobotOutlined,
  MessageOutlined,
  CalendarOutlined,
  MoonOutlined,
  LineChartOutlined,
  DollarOutlined,
  GlobalOutlined,
  SettingOutlined,
  TeamOutlined,
  BankOutlined,
  ThunderboltOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import './MainLayout.css';

const { Sider, Header, Content } = Layout;

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout, isOpcUser } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.info('You have been logged out');
    navigate('/login');
  };

  const getOpenKeysForPath = (pathname) => {
    const keys = [];
    if (pathname.startsWith('/reports')) keys.push('reports-group');
    if (['/daily-runs/overview', '/daily-runs/breakdown'].some(p => pathname.startsWith(p))) keys.push('opc-reports-group');

    if (['/payments', '/invoices', '/reconciliation'].some(p => pathname.startsWith(p))) keys.push('finance-group');
    if (['/daily-runs', '/pods', '/enquiries', '/fuel-runs'].some(p => pathname.startsWith(p))) keys.push('operations-group');
    if (['/drivers', '/vans'].some(p => pathname.startsWith(p))) keys.push('fleet-group');
    if (['/channels', '/agent-logs'].some(p => pathname.startsWith(p))) keys.push('settings-group');
    return keys;
  };

  const [openKeys, setOpenKeys] = useState(() => getOpenKeysForPath(location.pathname));

  useEffect(() => {
    const keys = getOpenKeysForPath(location.pathname);
    setOpenKeys(prev => [...new Set([...prev, ...keys])]);
  }, [location.pathname]);

  // Navigation items
  const navigation = isOpcUser
    ? [
        { key: '/daily-runs', label: <Link to="/daily-runs">Daily Runs</Link>, icon: <CalendarOutlined /> },
        {
          key: 'opc-reports-group',
          label: 'Reports',
          icon: <BarChartOutlined />,
          children: [
            { key: '/daily-runs/overview', label: <Link to="/daily-runs/overview">Overview</Link>, icon: <LineChartOutlined /> },
            { key: '/daily-runs/breakdown', label: <Link to="/daily-runs/breakdown">Breakdown</Link>, icon: <TableOutlined /> },
          ],
        },
        { key: '/daily-runs/drivers', label: <Link to="/daily-runs/drivers">Drivers</Link>, icon: <UserOutlined /> },
        { key: '/daily-runs/vans', label: <Link to="/daily-runs/vans">Vans</Link>, icon: <CarOutlined /> },
        { key: '/daily-runs/clients', label: <Link to="/daily-runs/clients">Clients</Link>, icon: <TeamOutlined /> },
        { key: '/fuel-runs', label: <Link to="/fuel-runs">Fuel Log</Link>, icon: <ThunderboltOutlined /> },
        { key: '/expenses', label: <Link to="/expenses">Expenses</Link>, icon: <WalletOutlined /> },
      ]
    : [
        {
          key: 'operations-group',
          label: 'Operations',
          icon: <AppstoreOutlined />,
          children: [
            { key: '/jobs', label: <Link to="/jobs">Jobs</Link>, icon: <AppstoreOutlined /> },
            { key: '/daily-runs', label: <Link to="/daily-runs">Daily Runs</Link>, icon: <CalendarOutlined /> },
            { key: '/fuel-runs', label: <Link to="/fuel-runs">Fuel Log</Link>, icon: <ThunderboltOutlined /> },
            { key: '/enquiries', label: <Link to="/enquiries">Enquiries</Link>, icon: <MessageOutlined /> },
          ],
        },
        {
          key: 'fleet-group',
          label: 'Fleet',
          icon: <TeamOutlined />,
          children: [
            { key: '/drivers', label: <Link to="/drivers">Drivers</Link>, icon: <UserOutlined /> },
            { key: '/vans', label: <Link to="/vans">Vans</Link>, icon: <CarOutlined /> },
          ],
        },
        ...(user?.role === 'admin' ? [
          {
            key: 'finance-group',
            label: 'Finance',
            icon: <BankOutlined />,
            children: [
              { key: '/payments', label: <Link to="/payments">Payments</Link>, icon: <span style={{ fontSize: '14px' }}>£</span> },
              { key: '/payments/overdue', label: <Link to="/payments/overdue">Overdue</Link>, icon: <BellOutlined style={{ color: 'red' }} /> },
              { key: '/invoices', label: <Link to="/invoices">Invoices</Link>, icon: <FileTextOutlined /> },
              { key: '/reconciliation', label: <Link to="/reconciliation">Reconciliation</Link>, icon: <ReconciliationOutlined /> },
            ],
          },
          {
            key: 'reports-group',
            label: 'Reports',
            icon: <BarChartOutlined />,
            children: [
              { key: '/reports', label: <Link to="/reports">Dashboard</Link>, icon: <LineChartOutlined /> },
              { key: '/reports/financial', label: <Link to="/reports/financial">Financial</Link>, icon: <span style={{ fontSize: '14px' }}>£</span> },
              { key: '/reports/revenue', label: <Link to="/reports/revenue">Revenue</Link>, icon: <DollarOutlined /> },
              { key: '/reports/van', label: <Link to="/reports/van">Fleet &amp; Resources</Link>, icon: <CarOutlined /> },
              { key: '/reports/job-map', label: <Link to="/reports/job-map">Job Map</Link>, icon: <GlobalOutlined /> },
              { key: '/reports/corridors', label: <Link to="/reports/corridors">Route Corridors</Link>, icon: <span style={{ fontSize: '14px' }}>↔</span> },
              { key: '/reports/demand', label: <Link to="/reports/demand">Demand by Day</Link>, icon: <CalendarOutlined /> },
            ],
          },
        ] : [
          { key: '/invoices', label: <Link to="/invoices">Invoices</Link>, icon: <FileTextOutlined /> },
        ]),
        {
          key: 'settings-group',
          label: 'Settings',
          icon: <SettingOutlined />,
          children: [
            { key: '/channels', label: <Link to="/channels">Channels</Link>, icon: <ClusterOutlined /> },
            { key: '/agent-logs', label: <Link to="/agent-logs">Agent Logs</Link>, icon: <RobotOutlined /> },
          ],
        },
      ];

  const selectedKey = location.pathname;

  // User dropdown menu
  const userMenu = (
    <Menu>
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>
        Logout
      </Menu.Item>
    </Menu>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        breakpoint="lg"
        width={240}
        style={{ background: '#001529' }}
      >
        <div className="logo-container" style={{ display: 'flex', alignItems: 'center', padding: 16 }}>
          <span className="logo-icon" style={{ color: 'white', fontSize: 28, marginRight: 8 }}>🚚</span>
          {!collapsed && (
            <span className="logo-text" style={{ color: 'white', fontWeight: 700, fontSize: 20 }}>
              {isOpcUser ? 'OPC' : 'LITE'}
            </span>
          )}
        </div>
        <div style={{
          padding: collapsed ? '0 8px 12px' : '0 16px 12px',
          marginBottom: 8,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          {!collapsed && (
            <>
              <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{user?.firstName} {user?.lastName}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2, textTransform: 'capitalize' }}>{user?.role || 'Administrator'}</div>
            </>
          )}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          items={navigation}
          style={{ borderRight: 0 }}
        />
      </Sider>
      <Layout>
        <Header style={{
          background: isDarkMode ? '#141414' : '#fff',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: isDarkMode ? '0 1px 4px rgba(0,21,41,0)' : '0 1px 4px rgba(0,21,41,.08)',
          borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0)'}`,
          transition: 'background 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: 20, marginLeft: 16 }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginRight: 24 }}>
            <Button type="text" icon={<BellOutlined />} />
            <Button
              type="text"
              icon={<MoonOutlined />}
              onClick={toggleDarkMode}
            />
            <Dropdown overlay={userMenu} placement="bottomRight">
              <Button type="text" icon={<UserOutlined />}>
                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
              </Button>
            </Dropdown>
          </div>
        </Header>
        <Content style={{ margin: 0, minHeight: 280, background: isDarkMode ? '#1f1f1f' : '#f5f6fa', transition: 'background 0.25s ease' }}>
          <div style={{ padding: 24 }}>
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;